import React from 'react';
import Header from '../components/Header';
import { Footer } from '../components/SharedLayout';

const WhoIsHudsonPage: React.FC = () => {
  const heading = 'rgb(15,15,15)';
  const sub = 'rgb(76,76,75)';
  const teal = '#145F6B';

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-24 sm:pt-28 pb-16">
        {/* Hero Section */}
        <section className="py-16 bg-gray-50">
          <div className="mx-auto max-w-[1000px] px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl sm:text-5xl font-semibold mb-6" style={{ color: heading }}>
              Who is Hudson?
            </h1>
            <div className="w-24 h-1 mx-auto mb-8" style={{ backgroundColor: teal }}></div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="mx-auto max-w-[1000px] px-4 sm:px-6 lg:px-8">
            <div className="prose prose-lg max-w-none">
              <p className="text-xl leading-relaxed mb-8" style={{ color: sub }}>
                Every home has moving parts. Maintenance, repairs, seasonal checks, vendors, warranties, projects—it's enough to make your house feel like a second job.
              </p>
              
              <p className="text-xl leading-relaxed mb-8 font-semibold" style={{ color: heading }}>
                Hudson changes that.
              </p>

              <p className="text-xl leading-relaxed mb-8" style={{ color: sub }}>
                Hudson is your Home Director. He's not the one swinging the hammer or installing the appliances. He's the one making sure those things get done right, on time, and with complete oversight.
              </p>

              {/* Key Points */}
              <div className="bg-gray-50 rounded-xl p-8 mb-8">
                <ul className="space-y-4 text-lg" style={{ color: sub }}>
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white font-semibold text-sm mr-4 mt-0.5" style={{ backgroundColor: teal }}>
                      •
                    </span>
                    <span>He anticipates problems before you even notice them.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white font-semibold text-sm mr-4 mt-0.5" style={{ backgroundColor: teal }}>
                      •
                    </span>
                    <span>He coordinates every vendor with precision and professionalism.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white font-semibold text-sm mr-4 mt-0.5" style={{ backgroundColor: teal }}>
                      •
                    </span>
                    <span>He documents everything, keeping a living history of your home so nothing slips through the cracks.</span>
                  </li>
                </ul>
              </div>

              <p className="text-xl leading-relaxed mb-8" style={{ color: sub }}>
                Think of Hudson as the COO of your household — orchestrating every moving part so you can finally enjoy your home the way it was meant to be: lived in, not managed.
              </p>

              <div className="text-center py-8">
                <p className="text-2xl font-semibold italic mb-4" style={{ color: teal }}>
                  With Hudson, your home is no longer a burden.
                </p>
                <p className="text-2xl font-semibold italic" style={{ color: teal }}>
                  It's a sanctuary.
                </p>
              </div>

              {/* Decorative Line */}
              <div className="text-center py-8">
                <div className="text-4xl font-light" style={{ color: teal }}>⸻</div>
              </div>
            </div>
          </div>
        </section>

        {/* Visual Elements */}
        <section className="py-16 bg-white">
          <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
              <div className="group">
                <div className="relative overflow-hidden rounded-xl shadow-lg group-hover:shadow-xl transition-shadow duration-300 aspect-[3/4]">
                  <img
                    src="https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/Joe.png"
                    alt="Joe Schafbuch, Founder"
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              </div>

              <div className="group">
                <div className="relative overflow-hidden rounded-xl shadow-lg group-hover:shadow-xl transition-shadow duration-300 aspect-[3/4]">
                  <img
                    src="https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/curtaincleaning.png"
                    alt="Hudson service in action"
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              </div>

              <div className="group">
                <div className="relative overflow-hidden rounded-xl shadow-lg group-hover:shadow-xl transition-shadow duration-300 aspect-[3/4]">
                  <img
                    src="https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/familyhugging.png"
                    alt="Family enjoying their home"
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-16 bg-gray-50">
          <div className="mx-auto max-w-[1000px] px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-semibold mb-4" style={{ color: heading }}>
              Ready to Experience the Hudson Difference?
            </h2>
            <p className="text-lg mb-8" style={{ color: sub }}>
              Let Hudson transform your home from a burden into a sanctuary.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+15152164433"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-lg text-white font-semibold transition-colors hover:opacity-90"
                style={{ backgroundColor: teal }}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
                Just Call Hudson
              </a>
              <a
                href="/services"
                className="inline-flex items-center justify-center px-8 py-3 rounded-lg border-2 font-semibold transition-colors hover:opacity-90"
                style={{ borderColor: teal, color: teal }}
              >
                View Our Services
              </a>
              <a
                href="/application"
                className="inline-flex items-center justify-center px-8 py-3 rounded-lg border-2 font-semibold transition-colors hover:opacity-90"
                style={{ borderColor: teal, color: teal }}
              >
                Start Your Application
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default WhoIsHudsonPage;
